import { RootState } from '../../store'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { logout } from '../login/loginSlice'

export type HideBalExp = { balance: boolean; exposer: boolean }

export type BalanceState = {
  balance: {
    balance: number
    exposer: number
    bonusBalance?: number
    availableBalance?: number
    pendingBonusUsed?: number
  }
  hide: HideBalExp
}

const initialState: BalanceState = {
  balance: {
    balance: 0,
    exposer: 0,
    bonusBalance: 0,
    availableBalance: 0,
    pendingBonusUsed: 0,
  },
  hide: { balance: false, exposer: false },
}

const balanceSlice = createSlice({
  name: 'balance',
  initialState,
  reducers: {
    setBalance: (
      state,
      action: PayloadAction<{
        balance: number
        exposer: number
        bonusBalance?: number
        availableBalance?: number
        pendingBonusUsed?: number
      }>,
    ) => {
      const nextBalance = action.payload.balance ?? state.balance.balance
      const nextExposer = action.payload.exposer ?? state.balance.exposer
      const nextPendingBonusUsed = action.payload.pendingBonusUsed ?? state.balance.pendingBonusUsed ?? 0
      const nextAvailable =
        action.payload.availableBalance ??
        Math.max(0, nextBalance - nextExposer + nextPendingBonusUsed)
      state.balance = {
        balance: nextBalance,
        exposer: nextExposer,
        bonusBalance: action.payload.bonusBalance ?? state.balance.bonusBalance ?? 0,
        availableBalance: nextAvailable,
        pendingBonusUsed: nextPendingBonusUsed,
      }
    },
    setExposer: (
      state,
      action: PayloadAction<number | { exposer: number; pendingBonusUsed?: number }>,
    ) => {
      const payload = action.payload
      const nextExposer = typeof payload === 'number' ? payload : payload.exposer
      // Prefer API pendingBonusUsed (placebet returns it after cashout exclusion).
      // Cap by exposer so a stale high pending can't inflate Available after a hedge.
      const rawPending =
        typeof payload === 'object' && payload.pendingBonusUsed !== undefined
          ? payload.pendingBonusUsed
          : state.balance.pendingBonusUsed ?? 0
      const pendingBonusUsed = Math.min(Math.max(0, rawPending), Math.max(0, nextExposer))
      state.balance = {
        ...state.balance,
        exposer: nextExposer,
        pendingBonusUsed,
        availableBalance: Math.max(
          0,
          (state.balance.balance ?? 0) - nextExposer + pendingBonusUsed,
        ),
      }
    },
    setSingleBal: (state, action: PayloadAction<number>) => {
      const pendingBonusUsed = state.balance.pendingBonusUsed ?? 0
      const nextBalance = action.payload
      const exposer = state.balance.exposer ?? 0
      const cappedPending = Math.min(Math.max(0, pendingBonusUsed), Math.max(0, exposer))
      state.balance = {
        ...state.balance,
        balance: nextBalance,
        pendingBonusUsed: cappedPending,
        availableBalance: Math.max(0, nextBalance - exposer + cappedPending),
      }
    },
    hideBalExp: (state, action: PayloadAction<HideBalExp>) => {
      state.hide = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logout, (state) => {
      state.balance = {
        balance: 0,
        exposer: 0,
        bonusBalance: 0,
        availableBalance: 0,
        pendingBonusUsed: 0,
      }
    })
  },
})

export const { setBalance, setExposer, hideBalExp, setSingleBal } = balanceSlice.actions
export const selectBalance = (state: RootState) => state.balanceReducer.balance
export const selectHideBalExp = (state: RootState) => state.balanceReducer.hide

export default balanceSlice
